import React, { useState, useEffect, useCallback, useRef } from 'react';

const GoogleAuth = ({ onAuthSuccess, onAuthFailure, onGuestAccess }) => {
  const [debugInfo, setDebugInfo] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loginError, setLoginError] = useState(null);
  const buttonContainerRef = useRef(null);

  // UTF-8 文字列をデコード
  const decodeUTF8 = (str) => {
    if (!str) return str;
    
    try {
      // Base64デコードされた文字列を正しくUTF-8としてデコード
      const bytes = new Uint8Array([...str].map(char => char.charCodeAt(0)));
      return new TextDecoder('utf-8').decode(bytes);
    } catch (e) {
      try {
        // 代替方法: escape/unescapeを使用
        return decodeURIComponent(escape(str));
      } catch (e2) {
        console.log('UTF-8 decode error, using original:', str);
        return str;
      }
    }
  };

  // Google Identity Services の初期化
  const initializeGoogle = useCallback(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    if (!window.google?.accounts?.id || !clientId) {
      console.log('Google not loaded or no client ID, retry count:', retryCount);
      if (retryCount < 3) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => initializeGoogle(), 2000); // 2秒後にリトライ
      }
      return;
    }

    try {
      // 既存の初期化をクリア
      if (isInitialized) {
        console.log('Re-initializing Google Sign-In...');
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            setLoginError(null); // エラーをクリア

            // JWT トークンをデコード (標準的な方法)
            const base64Url = response.credential.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            const payload = JSON.parse(jsonPayload);

            const userInfo = {
              id: payload.sub,
              name: payload.name || 'User',
              email: payload.email,
              picture: payload.picture,
              given_name: payload.given_name || '',
              family_name: payload.family_name || '',
            };

            console.log('Raw payload:', payload);
            console.log('Decoded user info:', userInfo);
            console.log('Name bytes:', payload.name ? [...payload.name].map(c => c.charCodeAt(0)) : 'no name');

            if (onAuthSuccess) {
              const result = await onAuthSuccess(userInfo, response.credential);
              // ログイン失敗の場合、エラーメッセージを表示
              if (result === false || (result && result.success === false)) {
                setLoginError(result?.message || 'ログインに失敗しました。もう一度お試しください。');
              }
            }
          } catch (error) {
            console.error('Authentication processing error:', error);
            setLoginError('認証処理中にエラーが発生しました。');
            if (onAuthFailure) {
              onAuthFailure(error);
            }
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Reactの管理外でボタンを作成
      const containerId = 'google-auth-container-' + Date.now();
      
      // body直下に一時的なコンテナを作成
      let externalContainer = document.getElementById('google-auth-external');
      if (!externalContainer) {
        externalContainer = document.createElement('div');
        externalContainer.id = 'google-auth-external';
        externalContainer.style.position = 'fixed';
        externalContainer.style.top = '-9999px';
        externalContainer.style.left = '-9999px';
        document.body.appendChild(externalContainer);
      }
      
      // ボタン要素を作成
      const buttonElement = document.createElement('div');
      buttonElement.id = containerId;
      externalContainer.appendChild(buttonElement);
      
      // Googleボタンをレンダリング
      setTimeout(() => {
        if (window.google?.accounts?.id && document.getElementById(containerId)) {
          try {
            window.google.accounts.id.renderButton(buttonElement, {
              theme: 'outline',
              size: 'large',
              text: 'signin_with',
              shape: 'rectangular',
              width: 250,
            });
            
            // レンダリング後にReactコンテナに直接移動（イベントリスナー保持）
            setTimeout(() => {
              if (buttonContainerRef.current && buttonElement.firstChild) {
                buttonContainerRef.current.innerHTML = '';
                // cloneではなく直接移動してイベントリスナーを保持
                buttonContainerRef.current.appendChild(buttonElement.firstChild);
              }
            }, 200);
          } catch (e) {
            console.error('Button render error:', e);
          }
        }
      }, 100);

      setIsInitialized(true);
      setRetryCount(0); // 成功時はリトライカウントをリセット
      console.log('Google Sign-In initialized successfully');
    } catch (error) {
      console.error('Google Sign-In initialization error:', error);
      // エラー時もリトライ
      if (retryCount < 3) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => initializeGoogle(), 2000);
      }
    }
  }, [onAuthSuccess, onAuthFailure, retryCount, isInitialized]);

  useEffect(() => {
    const info = {
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      googleLoaded: !!window.google,
      nodeEnv: process.env.NODE_ENV
    };
    setDebugInfo(info);

    // より積極的な初期化を行う
    const tryInitialize = () => {
      if (window.google?.accounts?.id) {
        console.log('Google loaded, initializing...');
        initializeGoogle();
        return true;
      }
      return false;
    };

    // 即座に試行
    if (tryInitialize()) {
      return;
    }

    // ポーリングで確認
    const checkGoogle = setInterval(() => {
      console.log('Checking for Google...');
      if (tryInitialize()) {
        clearInterval(checkGoogle);
      }
    }, 500); // 500ms間隔で確認

    // タイムアウト処理
    const timeout = setTimeout(() => {
      clearInterval(checkGoogle);
      if (!window.google) {
        console.error('Google Identity Services loading timeout');
        // タイムアウト後も再試行
        setTimeout(tryInitialize, 1000);
      }
    }, 15000); // 15秒でタイムアウト

    // クリーンアップ
    return () => {
      clearInterval(checkGoogle);
      clearTimeout(timeout);
      // 外部コンテナもクリーンアップ
      const externalContainer = document.getElementById('google-auth-external');
      if (externalContainer) {
        try {
          document.body.removeChild(externalContainer);
        } catch (e) {
          console.warn('External container cleanup error:', e);
        }
      }
    };
  }, [initializeGoogle]);

  const handleTestLogin = async () => {
    setLoginError(null); // エラーをクリア
    const testUser = {
      id: 'test-user-123',
      name: 'Test User',
      email: 'test@example.com',
      picture: 'https://via.placeholder.com/60'
    };

    if (onAuthSuccess) {
      const result = await onAuthSuccess(testUser, 'test-token');
      if (result === false || (result && result.success === false)) {
        setLoginError(result?.message || 'テストログインに失敗しました。開発環境でのみ利用可能です。');
      }
    }
  };

  const handleReinitialize = () => {
    setIsInitialized(false);
    setTimeout(() => {
      initializeGoogle();
    }, 100);
  };

  return (
    <div style={{ 
      padding: '40px', 
      textAlign: 'center',
      border: '1px solid #ddd',
      borderRadius: '8px',
      backgroundColor: '#f8f9fa'
    }}>
      <h2 style={{ marginBottom: '20px', color: '#333' }}>
        ログインが必要です
      </h2>

      {loginError && (
        <div style={{
          marginBottom: '20px',
          padding: '12px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          {loginError}
        </div>
      )}

      {process.env.NODE_ENV === 'development' && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '10px', 
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          fontSize: '12px',
          textAlign: 'left'
        }}>
          <strong>Debug Info:</strong><br/>
          Client ID: {debugInfo.clientId ? 'Set' : 'Not set'}<br/>
          Google Script: {debugInfo.googleLoaded ? 'Loaded' : 'Not loaded'}<br/>
          Initialized: {isInitialized ? 'Yes' : 'No'}<br/>
          Retry Count: {retryCount}/3<br/>
          Environment: {debugInfo.nodeEnv}<br/>
        </div>
      )}

      <div 
        ref={buttonContainerRef}
        style={{ 
          marginTop: '20px',
          marginBottom: '20px',
          minHeight: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {!isInitialized && !debugInfo.googleLoaded && 'Loading Google Sign-In...'}
        {!isInitialized && debugInfo.googleLoaded && retryCount > 0 && `Initializing... (attempt ${retryCount}/3)`}
        {!isInitialized && debugInfo.googleLoaded && retryCount === 0 && 'Preparing Google Sign-In...'}
      </div>

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
        {onGuestAccess && (
          <button
            onClick={onGuestAccess}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              marginTop: '10px'
            }}
          >
            ログインせずに利用する
          </button>
        )}

        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={handleTestLogin}
            style={{
              padding: '8px 16px',
              backgroundColor: '#adb5bd',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            [Dev] テストログイン
          </button>
        )}

        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={handleReinitialize}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Reload Google Sign-In
          </button>
        )}
      </div>
    </div>
  );
};

export default GoogleAuth;